import { useEffect, useRef } from 'react';

import usePersistent from 'hooks/use-persistent';
import isDeepEqualWith from 'utils/is-deep-equal-with';

interface Options<T> {
    subject: T;
    callback: (current: T, previous: T) => void;
    comparator?: (current: T, previous: T) => boolean;
    enabled?: boolean;
}

export default function useObserve<T>({ subject, callback, comparator = isDeepEqualWith, enabled = true }: Options<T>) {
    const previousRef = useRef(subject);
    const comparatorRef = usePersistent(comparator);
    const callbackRef = usePersistent(callback);

    useEffect(() => {
        if (enabled) {
            const hasUpdated = comparatorRef.current(previousRef.current, subject) === false;

            if (hasUpdated) {
                callbackRef.current(subject, previousRef.current);
            }
        }

        previousRef.current = subject;
    }, [callbackRef, comparatorRef, enabled, subject]);
}
